import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Navbar } from '@/components/Navbar';
import {
  INTERESTS, MYTHOLOGIES, LANGUAGE_GROUPS, getLanguage, AGE_GROUPS, GENDERS, STORY_LENGTHS,
  getMythology, getDeity,
} from '@/utils/mythologies';
import { generateStory, fetchStoryById } from '@/utils/api';
import { trackStoryGenerated } from '@/utils/analytics';
import { toast } from 'sonner';

const LOADING_MESSAGES = [
  "Placing your child in the ancient world",
  'Making both interests essential to the plot',
  'Your chosen deity is waiting in the story',
  "Giving every sentence your child's name and spirit",
];

/**
 * Poll GET /api/story/<id> every 2.5s until status becomes 'ready' or 'error'.
 * Rejects on timeout or backend error.
 */
async function pollForReadyStory(storyId, timeoutMs = 180000) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    // eslint-disable-next-line no-await-in-loop
    const s = await fetchStoryById(storyId);
    if (s.status === 'ready' && s.body && s.title) return s;
    if (s.status === 'error') {
      const err = new Error(s.error || 'Story generation failed');
      err.userMessage = s.error || 'Story generation failed';
      throw err;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 2500));
  }
  const err = new Error('Story generation timed out. Please try again.');
  err.userMessage = 'Story generation timed out. Please try again.';
  throw err;
}

export default function CreateWizard() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  // Step-3 has two sub-steps: 'mythology' → 'deity'
  const [step3Phase, setStep3Phase] = useState('mythology');

  const [childName, setChildName] = useState('');
  const [interests, setInterests] = useState([]);
  const [mythology, setMythology] = useState('');
  const [deity, setDeity] = useState('');
  const [age, setAge] = useState('5–7 years');
  const [gender, setGender] = useState('boy');
  const [language, setLanguage] = useState('English');
  const [lengthKey, setLengthKey] = useState('standard');
  const [addSibling, setAddSibling] = useState(false);
  const [siblingName, setSiblingName] = useState('');
  const [siblingInterests, setSiblingInterests] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);

  // Cycle loading messages
  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setLoadingMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length), 1800);
    return () => clearInterval(t);
  }, [loading]);

  const canNext = () => {
    if (step === 1) return childName.trim().length > 0;
    if (step === 2) return interests.length === 2;
    if (step === 3) {
      if (step3Phase === 'mythology') return !!mythology;
      return !!deity;
    }
    if (step === 4) {
      if (addSibling) return siblingName.trim().length > 0 && siblingInterests.length === 2;
      return true;
    }
    return false;
  };

  const goNext = () => {
    if (!canNext()) return;
    if (step === 3 && step3Phase === 'mythology') {
      setStep3Phase('deity');
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const goBack = () => {
    if (step === 3 && step3Phase === 'deity') {
      // stay on step 3, go back to mythology sub-step
      setStep3Phase('mythology');
      setDeity('');
      return;
    }
    if (step === 4) {
      // returning into step 3 should land on the deity sub-step (so the user can adjust deity)
      setStep(3);
      setStep3Phase('deity');
      return;
    }
    setStep((s) => Math.max(1, s - 1));
  };

  const toggleInterest = (interest, target = 'main') => {
    const list = target === 'main' ? interests : siblingInterests;
    const setter = target === 'main' ? setInterests : setSiblingInterests;
    if (list.includes(interest)) {
      setter(list.filter((i) => i !== interest));
    } else if (list.length < 2) {
      setter([...list, interest]);
    }
  };

  const onSelectMythology = (key) => {
    setMythology(key);
    setDeity(''); // reset deity when mythology changes
    // auto-advance to deity phase
    setStep3Phase('deity');
  };

  const doGenerate = async () => {
    setLoading(true);
    setLoadingMsgIdx(0);
    const chosenMyth = getMythology(mythology);
    const chosenDeity = getDeity(mythology, deity);
    const wordCount = STORY_LENGTHS.find((l) => l.key === lengthKey)?.words || 450;

    const payload = {
      child_name: childName.trim(),
      age,
      gender,
      interest_1: interests[0]?.toLowerCase() || '',
      interest_2: interests[1]?.toLowerCase() || '',
      mythology_name: mythology,
      deity_name: deity,
      language,
      word_count: wordCount,
    };
    if (addSibling) {
      payload.sibling = {
        name: siblingName.trim(),
        interest_1: siblingInterests[0]?.toLowerCase() || '',
        interest_2: siblingInterests[1]?.toLowerCase() || '',
      };
    }

    try {
      const pending = await generateStory(payload);
      // Poll until the background job produces the story body (or errors)
      const readyStory = await pollForReadyStory(pending.id, 180000);
      // GA4: story_generated event
      trackStoryGenerated({
        storyId: readyStory.id,
        childName: readyStory.child_name || payload.child_name,
        mythology: readyStory.mythology || payload.mythology_name,
        deity: readyStory.deity || payload.deity_name,
        language: readyStory.language || payload.language,
        wordCount: readyStory.word_count,
        hasSibling: !!payload.sibling,
      });
      sessionStorage.setItem('tl:currentStory', JSON.stringify({
        story: readyStory,
        input: payload,
        mythology_meta: chosenMyth,
        deity_meta: chosenDeity,
      }));
      navigate(`/story/${readyStory.id}`);
    } catch (e) {
      setLoading(false);
      const msg = e?.userMessage || e?.message || 'Story generation failed. Please try again.';
      toast.error(msg, { duration: 6000 });
      // eslint-disable-next-line no-console
      console.error('Story generation failed:', msg, e);
    }
  };

  const chosenMyth = getMythology(mythology);
  const chosenDeity = getDeity(mythology, deity);

  return (
    <div className="min-h-screen bg-tl-hero text-white">
      <Navbar variant="dark" />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-4 pb-16">
        {/* Header row: back + step counter */}
        <div className="flex items-center justify-between">
          {(step > 1 || (step === 3 && step3Phase === 'deity')) && !loading ? (
            <button
              data-testid="wizard-back-button"
              onClick={goBack}
              className="text-tl-textSecondaryOnDark hover:text-white text-[13px] inline-flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
          ) : <span />}
          <div data-testid="wizard-step-counter" className="text-[11px] uppercase tracking-[0.14em] text-tl-textSecondaryOnDark">
            Step {step} of 4
          </div>
        </div>

        {/* Segmented progress */}
        <div data-testid="wizard-progress" className="mt-3 flex items-center gap-2">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className={`h-2 flex-1 rounded-full ${n < step ? 'bg-tl-purple' : n === step ? 'bg-tl-gold' : 'bg-white/10'}`}
            />
          ))}
        </div>

        {loading ? (
          <LoadingState mythology={chosenMyth} deity={chosenDeity} childName={childName} msgIdx={loadingMsgIdx} />
        ) : (
          <div className="mt-8 bg-tl-card border border-tl-border rounded-tlLg p-5 sm:p-6 shadow-tl-card">
            {step === 1 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-tl-textSecondaryOnDark">Step 1 of 4 · Child's name</div>
                <h2 className="mt-2 text-[22px] font-semibold text-white">What is your child's name?</h2>
                <div className="mt-5">
                  <Label htmlFor="childName" className="text-tl-textSecondaryOnDark text-[12px]">Name</Label>
                  <Input
                    id="childName"
                    data-testid="wizard-child-name-input"
                    autoFocus
                    value={childName}
                    onChange={(e) => setChildName(e.target.value.slice(0, 30))}
                    placeholder="Aarav, Priya, Emma, Noah, Yuki…"
                    className="mt-2 h-12 bg-tl-dark2 border-tl-border text-white placeholder:text-tl-textSecondaryOnDark/70"
                  />
                  <p className="mt-2 text-[11px] text-tl-textSecondaryOnDark">
                    Their name will appear in every sentence — this is what makes it magical.
                  </p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-tl-textSecondaryOnDark">Step 2 of 4 · Interests</div>
                <h2 className="mt-2 text-[22px] font-semibold text-white">What does {childName || 'your child'} love?</h2>
                <div className="mt-1 text-[12px] text-tl-textSecondaryOnDark">(Pick exactly 2)</div>
                <div data-testid="wizard-interests-chip-group" className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {INTERESTS.map((it) => {
                    const selected = interests.includes(it);
                    const disabled = !selected && interests.length >= 2;
                    return (
                      <button
                        key={it}
                        type="button"
                        data-testid={`wizard-interest-chip-${it.toLowerCase()}`}
                        onClick={() => !disabled && toggleInterest(it, 'main')}
                        disabled={disabled}
                        className={`h-10 px-3 rounded-full border text-[13px] transition-colors ${
                          selected
                            ? 'bg-tl-purple border-transparent text-white'
                            : disabled
                              ? 'bg-tl-dark2 border-tl-border text-tl-textSecondaryOnDark opacity-40 cursor-not-allowed'
                              : 'bg-tl-dark2 border-tl-border text-tl-textSecondaryOnDark hover:bg-white/5'
                        }`}
                      >
                        {it}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && step3Phase === 'mythology' && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-tl-textSecondaryOnDark">Step 3 of 4 · Mythology tradition</div>
                <h2 className="mt-2 text-[22px] font-semibold text-white">Choose a mythology tradition</h2>
                <div className="mt-1 text-[12px] text-tl-textSecondaryOnDark">Next, you'll pick a specific deity from this tradition.</div>
                <div data-testid="wizard-mythology-grid" className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {MYTHOLOGIES.map((m) => {
                    const selected = mythology === m.key;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        data-testid={`wizard-mythology-card-${m.key.toLowerCase().replace(/[^a-z]/g, '')}`}
                        onClick={() => onSelectMythology(m.key)}
                        className={`tl-mythology-card text-left rounded-tlLg border p-4 outline-none focus-visible:ring-2 focus-visible:ring-tl-gold min-h-[120px] ${
                          selected
                            ? 'bg-tl-purple/20 border-tl-purple text-white'
                            : 'bg-tl-dark2 border-tl-border text-white hover:border-tl-purple/70'
                        }`}
                      >
                        <div className="text-[22px] leading-none">{m.flag}</div>
                        <div className="mt-2 text-[14px] font-semibold">{m.key}</div>
                        <div className="text-[11px] text-tl-textSecondaryOnDark mt-1">{m.deities.length} deities</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && step3Phase === 'deity' && chosenMyth && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-tl-textSecondaryOnDark">Step 3 of 4 · {chosenMyth.key} deity</div>
                <h2 className="mt-2 text-[22px] font-semibold text-white">Which {chosenMyth.key} deity will guide {childName || 'your child'}?</h2>
                <div className="mt-1 text-[12px] text-tl-textSecondaryOnDark">{chosenMyth.tagline}</div>
                <div data-testid="wizard-deity-grid" className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {chosenMyth.deities.map((d) => {
                    const selected = deity === d.name;
                    return (
                      <button
                        key={d.name}
                        type="button"
                        data-testid={`wizard-deity-card-${d.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                        onClick={() => setDeity(d.name)}
                        className={`tl-mythology-card text-left rounded-tlLg border p-4 outline-none focus-visible:ring-2 focus-visible:ring-tl-gold min-h-[112px] ${
                          selected
                            ? 'bg-tl-purple/25 border-tl-purple text-white'
                            : 'bg-tl-dark2 border-tl-border text-white hover:border-tl-purple/70'
                        }`}
                      >
                        <div className="text-[22px] leading-none">{d.emoji}</div>
                        <div className="mt-2 text-[14px] font-semibold">{d.name}</div>
                        <div className="text-[11px] text-tl-textSecondaryOnDark mt-1 leading-snug">{d.blurb}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-tl-textSecondaryOnDark">Step 4 of 4 · Final details</div>
                <h2 className="mt-2 text-[22px] font-semibold text-white">Almost there</h2>
                {chosenDeity && (
                  <div className="mt-2 text-[12px] text-tl-textSecondaryOnDark">
                    Your story: <span className="text-white">{childName}</span> with{' '}
                    <span className="text-tl-gold">{chosenDeity.emoji} {chosenDeity.name}</span> ({chosenMyth?.key})
                  </div>
                )}

                {/* PROMINENT: Story language */}
                <div className="mt-6 bg-tl-dark2 border border-tl-purple/40 rounded-tlLg p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-tl-gold" />
                        <div className="text-[11px] uppercase tracking-[0.14em] text-tl-gold">Story language</div>
                      </div>
                      <div className="mt-1 text-[13px] text-tl-textSecondaryOnDark">
                        Your child's story will be written entirely in this language.
                      </div>
                    </div>
                    {getLanguage(language) && (
                      <div className="hidden sm:flex items-center gap-1.5 text-[12px] text-tl-textSecondaryOnDark">
                        <span className="text-white font-medium">{getLanguage(language).name}</span>
                        {getLanguage(language).name !== getLanguage(language).native && (
                          <span>· {getLanguage(language).native}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="wizard-language-select" className="mt-3 bg-tl-hero border-tl-border text-white h-12 text-[14px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[360px]">
                      {LANGUAGE_GROUPS.map((group) => (
                        <SelectGroup key={group.region}>
                          <SelectLabel className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{group.region}</SelectLabel>
                          {group.languages.map((lng) => (
                            <SelectItem key={lng.name} value={lng.name} data-testid={`wizard-language-option-${lng.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                              <span className="flex items-baseline gap-2">
                                <span className="font-medium">{lng.name}</span>
                                {lng.name !== lng.native && (
                                  <span className="text-slate-500 text-[12px]">{lng.native}</span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[12px] text-tl-textSecondaryOnDark">Age</Label>
                    <Select value={age} onValueChange={setAge}>
                      <SelectTrigger data-testid="wizard-age-select" className="mt-2 bg-tl-dark2 border-tl-border text-white h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AGE_GROUPS.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[12px] text-tl-textSecondaryOnDark">Gender</Label>
                    <Select value={gender} onValueChange={setGender}>
                      <SelectTrigger data-testid="wizard-gender-select" className="mt-2 bg-tl-dark2 border-tl-border text-white h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map((g) => (<SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-[12px] text-tl-textSecondaryOnDark">Story length</Label>
                  <Select value={lengthKey} onValueChange={setLengthKey}>
                    <SelectTrigger data-testid="wizard-length-select" className="mt-2 bg-tl-dark2 border-tl-border text-white h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STORY_LENGTHS.map((s) => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-5 flex items-center justify-between bg-tl-dark2 border border-tl-border rounded-tl p-4">
                  <div>
                    <div className="text-[13px] text-white font-medium">Add a sibling?</div>
                    <div className="text-[11px] text-tl-textSecondaryOnDark">Both become heroes in one story.</div>
                  </div>
                  <Switch data-testid="wizard-sibling-toggle" checked={addSibling} onCheckedChange={setAddSibling} />
                </div>

                {addSibling && (
                  <div className="mt-4 space-y-4 border border-tl-border rounded-tl p-4 bg-tl-dark2/50">
                    <div>
                      <Label className="text-[12px] text-tl-textSecondaryOnDark">Sibling's name</Label>
                      <Input
                        data-testid="wizard-sibling-name-input"
                        value={siblingName}
                        onChange={(e) => setSiblingName(e.target.value.slice(0, 30))}
                        placeholder="Sibling's name"
                        className="mt-2 h-11 bg-tl-hero border-tl-border text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-[12px] text-tl-textSecondaryOnDark">Sibling's interests (pick 2)</Label>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {INTERESTS.map((it) => {
                          const selected = siblingInterests.includes(it);
                          const disabled = !selected && siblingInterests.length >= 2;
                          return (
                            <button
                              key={it}
                              type="button"
                              data-testid={`wizard-sibling-interest-${it.toLowerCase()}`}
                              onClick={() => !disabled && toggleInterest(it, 'sibling')}
                              disabled={disabled}
                              className={`h-9 px-2 rounded-full border text-[12px] transition-colors ${
                                selected ? 'bg-tl-purple border-transparent text-white' :
                                disabled ? 'opacity-40 cursor-not-allowed bg-tl-hero border-tl-border text-tl-textSecondaryOnDark' :
                                'bg-tl-hero border-tl-border text-tl-textSecondaryOnDark hover:bg-white/5'
                              }`}
                            >
                              {it}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Nav buttons */}
            <div className="mt-8 flex items-center justify-end">
              {step < 4 && (
                <Button
                  data-testid="wizard-next-button"
                  onClick={goNext}
                  disabled={!canNext()}
                  className="h-12 px-5 bg-tl-purple hover:bg-tl-purpleDark text-white text-[13px] gap-2 disabled:opacity-40"
                >
                  Continue <Sparkles className="h-4 w-4" />
                </Button>
              )}
              {step === 4 && (
                <Button
                  data-testid="wizard-generate-button"
                  onClick={doGenerate}
                  disabled={!canNext()}
                  className="h-12 px-6 bg-tl-success hover:bg-tl-success/90 text-white text-[13px] gap-2 disabled:opacity-40"
                >
                  <Sparkles className="h-4 w-4" /> Write {childName || 'your child'}'s mythology story
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingState({ mythology, deity, childName, msgIdx }) {
  const emoji = deity?.emoji || '✨';
  const msg = LOADING_MESSAGES[msgIdx] || LOADING_MESSAGES[0];
  const deityName = deity?.name || '';
  const mythName = mythology?.key || 'mythology';
  return (
    <div data-testid="wizard-loading-state" className="mt-16 text-center">
      <div className="inline-block text-[64px] animate-tl-loading-deity" aria-hidden="true">{emoji}</div>
      <div className="mt-6 text-[16px] sm:text-[18px] font-semibold text-white">
        Writing {childName ? `${childName}'s ` : ''}{mythName} bedtime story{deityName ? ` with ${deityName}` : ''}…
      </div>
      <div className="mt-2 text-[13px] text-tl-textSecondaryOnDark min-h-[20px] transition-opacity duration-300">
        {msg}
      </div>
      <div className="mt-6 flex items-center justify-center gap-2" aria-hidden="true">
        <span className="w-2 h-2 rounded-full bg-tl-gold animate-tl-loading-dot" />
        <span className="w-2 h-2 rounded-full bg-tl-gold animate-tl-loading-dot" style={{ animationDelay: '0.2s' }} />
        <span className="w-2 h-2 rounded-full bg-tl-gold animate-tl-loading-dot" style={{ animationDelay: '0.4s' }} />
      </div>
    </div>
  );
}
